import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';
import SmtpGatewayCore from '../components/SmtpGatewayCore';
import { SmtpConfig } from '../widget/types';

interface SmtpData {
  smtp_mode: 'default' | 'custom' | 'byos' | 'disabled';
  config: SmtpConfig | null;
  config_error: string | null;
}

const SmtpGatewayPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspaceId = parseInt(id || '0', 10);
  const { showSuccess, showError } = useToast();

  const [workspace, setWorkspace] = useState<any>(null);
  const [data, setData] = useState<SmtpData>({ smtp_mode: 'default', config: null, config_error: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [wsResponse, smtpResponse] = await Promise.all([
        apiService.get(`/workspaces/${workspaceId}`),
        apiService.get(`/workspaces/${workspaceId}/smtp`),
      ]);
      setWorkspace(wsResponse.data);
      setData(smtpResponse.data);
    } catch (err: any) {
      showError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const smtpResponse = await apiService.get(`/workspaces/${workspaceId}/smtp`);
      setData(smtpResponse.data);
    } catch (err: any) {
      showError(err.message || 'Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSave = async (config: SmtpConfig) => {
    await apiService.put(`/workspaces/${workspaceId}/smtp`, { smtp: config });
    const smtpResponse = await apiService.get(`/workspaces/${workspaceId}/smtp`);
    setData(smtpResponse.data);
  };

const handleReset = async () => {
await apiService.delete(`/workspaces/${workspaceId}/smtp`, { mode: 'default' });
    const smtpResponse = await apiService.get(`/workspaces/${workspaceId}/smtp`);
    setData(smtpResponse.data);
  };

  const handleDisable = async () => {
    await apiService.delete(`/workspaces/${workspaceId}/smtp`, { mode: 'disabled' });
    const smtpResponse = await apiService.get(`/workspaces/${workspaceId}/smtp`);
    setData(smtpResponse.data);
  };

  const handleTest = async (config: SmtpConfig, testRecipient: string) => {
    const response = await apiService.post(`/workspaces/${workspaceId}/smtp/test`, {
      smtp: config,
      test_recipient: testRecipient,
    });
    return response.data as { success: boolean; error?: string };
  };

  return (
    <div
      style={{
        padding: '32px',
        backgroundColor: 'var(--v2-bg)',
        minHeight: '100vh',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Breadcrumbs */}
      <nav
        style={{
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
        }}
      >
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: '0.875rem',
            color: 'var(--v2-text-tertiary)',
            fontWeight: 500,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--v2-primary)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
        >
          Workspaces
        </button>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text)', fontWeight: 500 }}>
          {workspace?.name}
        </span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>SMTP Gateway</span>
      </nav>

      <SmtpGatewayCore
        smtpMode={data.smtp_mode}
        config={data.config}
        configError={data.config_error}
        loading={loading}
        error={null}
        refreshing={refreshing}
        mode="modal"
        onSave={handleSave}
onReset={handleReset}
        onDisable={handleDisable}
        onTest={handleTest}
        onRefresh={handleRefresh}
        onSuccess={showSuccess}
        onError={showError}
      />
    </div>
  );
};

export default SmtpGatewayPage;
